import { createTrainingEngine } from "./engine";

// Host-side coordination, still independent of React and browser timers.
// Entries survive navigation in memory; leaving the last view pauses and releases the lease.
export function createCoachStore({ clock, createAudio, acquireLease = async () => () => {}, idFactory }) {
  const entries = new Map();
  const activityListeners = new Set();
  let owner = null;
  const active = entry => ["countdown", "running"].includes(entry.engine.getSnapshot().state);
  const isActive = () => [...entries.values()].some(entry => active(entry) || entry.busy);
  const notifyActivity = () => activityListeners.forEach(listener => listener(isActive()));
  const view = entry => ({ ...entry.engine.getSnapshot(), busy: entry.busy, audioAvailable: entry.audioAvailable,
    blocked: entry.blocked, mode: entry.mode });
  const notify = entry => { entry.listeners.forEach(listener => listener(view(entry))); notifyActivity(); };
  function release(entry) {
    entry.generation += 1; entry.audioGeneration += 1; entry.busy = false; entry.audio.stop();
    entry.releaseLease?.(); entry.releaseLease = null;
    if (owner === entry) owner = null;
    notifyActivity();
  }
  function connect(key, plan, listener) {
    let entry = entries.get(key);
    const signature = JSON.stringify(plan);
    if (entry && entry.signature !== signature) {
      entry.engine.abort(); release(entry); entry.unsubscribeAudio?.(); entry.audio.dispose();
      entry.engine = createTrainingEngine({ plan, clock, sessionId: idFactory() });
      entry.signature = signature; entry.audio = createAudio(); entry.audioAvailable = null;
      entry.mode = "tones";
      entry.blocked = false; observeAudio(entry); notify(entry);
    }
    if (!entry) {
      entry = { key, signature, engine: createTrainingEngine({ plan, clock, sessionId: idFactory() }), audio: createAudio(),
        listeners: new Set(), generation: 0, audioGeneration: 0, busy: false, blocked: false, audioAvailable: null, mode: "tones", language: "de", releaseLease: null };
      observeAudio(entry);
      entries.set(key, entry);
    }
    entry.listeners.add(listener); listener(view(entry));
    // Bound retained sessions without discarding a visible or active training.
    if (entries.size > 20) for (const [oldKey, old] of entries) {
      if (entries.size <= 20) break;
      if (old !== entry && !old.listeners.size && !active(old) && !old.busy) { release(old); old.unsubscribeAudio?.(); old.audio.dispose(); entries.delete(oldKey); }
    }
    return () => {
      entry.listeners.delete(listener);
      if (!entry.listeners.size) { entry.engine.pause("navigation"); release(entry); }
    };
  }
  function observeAudio(entry) {
    entry.unsubscribeAudio = entry.audio.subscribeInterruption?.(() => {
      if (!active(entry) && !entry.busy) return;
      entry.engine.pause("audio"); entry.audioAvailable = false; release(entry); notify(entry);
    });
  }
  function finish(entry) {
    if (entry.engine.getSnapshot().state !== "completed") { release(entry); return; }
    const generation = entry.generation;
    // Keep the cross-tab lease until the actual final signal is idle.
    Promise.resolve(entry.audio.whenIdle?.()).then(() => {
      if (generation !== entry.generation || entry.engine.getSnapshot().state !== "completed") return;
      entry.releaseLease?.(); entry.releaseLease = null;
      if (owner === entry) owner = null;
    });
  }
  const deliver = entry => {
    const cues = entry.engine.drainCues();
    if (entry.mode !== "muted" && entry.audioAvailable) entry.audio.playBatch(cues, { language: entry.language, mode: entry.mode, nowMs: clock.now() });
  };
  async function activate(key, action, options = {}) {
    const entry = entries.get(key);
    if (!entry || !entry.listeners.size || entry.busy || (action === "start" ? entry.engine.getSnapshot().state !== "ready" : entry.engine.getSnapshot().state !== "paused")) return false;
    const { mode = entry.mode, language = entry.language } = options;
    if (owner && owner !== entry && owner.engine.getSnapshot().state === "completed") release(owner);
    if (owner && owner !== entry) { entry.blocked = true; notify(entry); return false; }
    owner = entry; entry.busy = true; entry.blocked = false; entry.mode = mode; entry.language = language;
    if (action === "start") entry.audio.beginSession?.();
    const generation = ++entry.generation;
    notify(entry);
    // Invoke audio unlock synchronously inside the host's user gesture, before awaiting the lease.
    let audioReady;
    try { audioReady = mode === "muted" ? Promise.resolve(false) : Promise.resolve(entry.audio.unlock({ userGesture: true })).catch(() => false); }
    catch { audioReady = Promise.resolve(false); }
    let lease;
    try { lease = await acquireLease(key); } catch { lease = null; }
    if (generation !== entry.generation || !entry.listeners.size) { lease?.(); return false; }
    if (!lease) { entry.blocked = true; release(entry); notify(entry); return false; }
    entry.releaseLease = lease;
    const audioAvailable = await audioReady;
    if (generation !== entry.generation || !entry.listeners.size) { lease?.(); return false; }
    entry.busy = false;
    entry.audioAvailable = audioAvailable;
    entry.engine[action](); deliver(entry);
    if (!active(entry)) release(entry);
    notify(entry); return active(entry);
  }
  function command(key, action, reason) {
    const entry = entries.get(key); if (!entry) return;
    entry.engine[action](reason);
    if (action === "reset") { entry.mode = "tones"; entry.audioAvailable = null; }
    if (["pause", "abort", "reset"].includes(action)) release(entry);
    else {
      deliver(entry);
      if (!active(entry)) finish(entry);
    }
    notify(entry);
  }
  async function setAudioEnabled(key, enabled, { language } = {}) {
    const entry = entries.get(key);
    if (!entry || !entry.listeners.size || entry.busy || !["ready", "countdown", "running", "paused"].includes(entry.engine.getSnapshot().state)) return false;
    const mode = enabled ? "tones" : "muted";
    if (entry.mode === mode) return entry.audioAvailable;
    const generation = ++entry.audioGeneration;
    entry.mode = mode; entry.audioAvailable = enabled ? null : false;
    if (language) entry.language = language;
    entry.engine.drainCues(); // Never replay cues produced while muted.
    if (!enabled) { entry.audio.stop(); notify(entry); return true; }
    if (entry.engine.getSnapshot().state === "ready") { notify(entry); return true; }
    notify(entry);
    let available = false;
    try { available = await entry.audio.unlock({ userGesture: true }); } catch { /* Silent timer remains usable. */ }
    if (generation !== entry.audioGeneration || !entry.listeners.size) return false;
    entry.audioAvailable = Boolean(available); notify(entry); return entry.audioAvailable;
  }
  function tick(key) {
    const entry = entries.get(key); if (!entry || !active(entry)) return;
    entry.engine.tick(); deliver(entry);
    if (!active(entry)) finish(entry);
    notify(entry);
  }
  function suspendAll(reason = "background") {
    for (const entry of entries.values()) if (active(entry) || entry.busy) {
      entry.engine.pause(reason); release(entry); notify(entry);
    }
    for (const entry of entries.values()) if (entry.engine.getSnapshot().state === "completed") release(entry);
  }
  function dispose() {
    for (const entry of entries.values()) { entry.engine.abort(); release(entry); entry.unsubscribeAudio?.(); entry.audio.dispose(); }
    entries.clear();
  }
  return { connect, isActive, subscribeActivity: listener => { activityListeners.add(listener); listener(isActive()); return () => activityListeners.delete(listener); },
    start: (key, options) => activate(key, "start", options), resume: (key, options) => activate(key, "resume", options),
    pause: key => command(key, "pause"), abort: key => command(key, "abort"), reset: key => command(key, "reset"),
    nextSection: key => command(key, "nextSection"), setAudioEnabled, tick, suspendAll, dispose };
}
